/**
 * VAVCI · Slider horizontal de la sección Productos.
 *
 * Mejora progresiva: el track (`[data-vprod-track]`) es un scroll horizontal nativo
 * con scroll-snap (usable con swipe/trackpad sin JS). Este módulo añade, por cada
 * `[data-vprod-slider]`: flechas prev/next (revelándolas con `is-enhanced` solo si hay
 * overflow), arrastrar-para-desplazar con ratón, y el estado disabled de las flechas
 * en los extremos. Recalcula en scroll/resize y vía ResizeObserver — así un slider
 * dentro de un panel de pestaña oculto (ancho 0) se ajusta al mostrarse.
 *
 * Multi-instancia e idempotente. Editor-aware (`shopify:section:load`).
 */
const HAS_DOM = typeof document !== 'undefined' && typeof window !== 'undefined';
const REDUCE = HAS_DOM && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * Pure geometry for the progress bar (`slider_nav: 'bar'`).
 * Returns the thumb width (visible fraction) and its offset (scroll fraction),
 * both in [0, 1] with offset + width <= 1. No overflow -> full-width thumb at 0.
 */
export function progressState(scrollLeft, scrollWidth, clientWidth) {
  if (!(scrollWidth > 0) || !(clientWidth > 0) || scrollWidth <= clientWidth) {
    return { width: 1, offset: 0 };
  }
  const width = Math.min(1, Math.max(0, clientWidth / scrollWidth));
  const raw = (Number(scrollLeft) || 0) / scrollWidth;
  const offset = Math.min(1 - width, Math.max(0, raw));
  return { width, offset };
}

function initSlider(slider) {
  if (slider.dataset.vprodSliderInit === '1') return;
  const track = slider.querySelector('[data-vprod-track]');
  if (!track) return;
  slider.dataset.vprodSliderInit = '1';

  const prev = slider.querySelector('[data-vprod-prev]');
  const next = slider.querySelector('[data-vprod-next]');
  const thumb = slider.querySelector('[data-vprod-progress-thumb]');

  function pageStep() {
    return Math.max(track.clientWidth * 0.9, 240);
  }

  function update() {
    const max = track.scrollWidth - track.clientWidth;
    slider.classList.toggle('is-enhanced', max > 4);
    if (prev) prev.disabled = track.scrollLeft <= 2;
    if (next) next.disabled = track.scrollLeft >= max - 2;
    if (thumb) {
      const { width, offset } = progressState(track.scrollLeft, track.scrollWidth, track.clientWidth);
      thumb.style.setProperty('--vprod-progress-w', String(width));
      thumb.style.setProperty('--vprod-progress-x', String(offset));
    }
  }

  if (prev) {
    prev.addEventListener('click', () => {
      track.scrollBy({ left: -pageStep(), behavior: REDUCE ? 'auto' : 'smooth' });
    });
  }
  if (next) {
    next.addEventListener('click', () => {
      track.scrollBy({ left: pageStep(), behavior: REDUCE ? 'auto' : 'smooth' });
    });
  }

  track.addEventListener('scroll', update, { passive: true });
  // El ResizeObserver sobre el track cubre también los cambios de viewport (el track
  // es responsive), así que no hace falta un listener de window.resize (que además se
  // fugaría al recargar la sección en el editor).
  if ('ResizeObserver' in window) {
    new ResizeObserver(update).observe(track);
  }

  // Arrastrar-para-desplazar con ratón (el táctil usa el scroll nativo).
  let down = false;
  let dragging = false;
  let startX = 0;
  let startLeft = 0;
  let moved = false;
  let pid = null;

  track.addEventListener('pointerdown', (event) => {
    if (event.pointerType === 'touch') return;
    down = true;
    dragging = false;
    moved = false;
    startX = event.clientX;
    startLeft = track.scrollLeft;
    pid = event.pointerId;
  });
  track.addEventListener('pointermove', (event) => {
    if (!down) return;
    const dx = event.clientX - startX;
    if (!dragging) {
      // Aún por debajo del umbral: NO es un arrastre. Dejamos que el click nativo
      // llegue al botón "Añadir" y a los enlaces de la tarjeta.
      if (Math.abs(dx) <= 3) return;
      dragging = true;
      moved = true;
      track.classList.add('is-dragging');
      // Solo AHORA capturamos el puntero, para que el arrastre continúe aunque el
      // cursor salga de la fila baja del track. NO se hace en pointerdown a propósito:
      // setPointerCapture redirige el click al track y anularía el botón "Añadir".
      try { track.setPointerCapture(pid); } catch (e) { /* no-op */ }
    }
    track.scrollLeft = startLeft - dx;
  });
  function endDrag() {
    if (!down) return;
    down = false;
    dragging = false;
    track.classList.remove('is-dragging');
    if (pid != null) {
      try { track.releasePointerCapture(pid); } catch (e) { /* no-op */ }
      pid = null;
    }
  }
  track.addEventListener('pointerup', endDrag);
  track.addEventListener('pointercancel', endDrag);
  // Sin captura hasta cruzar el umbral: si el ratón sale del track antes de arrastrar,
  // cerramos el gesto para no dejar `down` colgado.
  track.addEventListener('pointerleave', endDrag);
  // Evita que un arrastre dispare el click de "Añadir" o el enlace de la tarjeta.
  track.addEventListener(
    'click',
    (event) => {
      if (moved) {
        event.preventDefault();
        event.stopPropagation();
        moved = false;
      }
    },
    true
  );

  update();
}

function boot() {
  document.querySelectorAll('[data-vprod-slider]').forEach(initSlider);
}

if (HAS_DOM) {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  document.addEventListener('shopify:section:load', boot);
}
